import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { CountrySelectionList } from '../../../../components/CountrySelectionList';
import type { ContactsCountByCountry } from '../../../../domain/contact';
import type { Country } from '../../../../domain/country';
import { StyledFilterSummary, StyledFilterName, StyledNotEmptyIndicator, StyledFilterDetails } from '../styles';

export type CountryFilterProps = {
  isOpen: boolean;
  onToggle(): void;
  selectedCountries: Country[];
  initiallySelectedCountries: Country[];
  onChange(countries: Country[]): void;
  contactsCountByCountries: ContactsCountByCountry[];
};

export const CountryFilter = (props: CountryFilterProps) => {
  const { isOpen, onToggle, selectedCountries, onChange, contactsCountByCountries, initiallySelectedCountries } = props;
  const { t } = useTranslation();
  const countries = useMemo(() => contactsCountByCountries.map((c) => c.country), [contactsCountByCountries]);
  const selectedCountryIdsSet = useMemo(
    () => new Set(selectedCountries.map((country) => country.id)),
    [selectedCountries]
  );
  const initiallyNotSelectedCountries = useMemo(() => {
    const initiallySelectedCountryIdsSet = new Set(initiallySelectedCountries.map((country) => country.id));
    return countries.filter((country) => !initiallySelectedCountryIdsSet.has(country.id));
  }, [initiallySelectedCountries, countries]);

  const handleClickOnCountry = (country: Country) => {
    if (selectedCountryIdsSet.has(country.id)) {
      onChange(selectedCountries.filter((c) => c.id !== country.id));
    } else {
      onChange(selectedCountries.concat(country));
    }
  };

  const isNotEmpty = selectedCountries.length > 0;

  return (
    <div data-automation="CountryFilter" aria-expanded={isOpen}>
      <StyledFilterSummary isOpen={isOpen} onClick={onToggle}>
        <StyledFilterName data-automation="CountryFilterName">{t('contacts.filters.countries.name')}</StyledFilterName>
        {isNotEmpty ? <StyledNotEmptyIndicator /> : null}
        <SWReactIcons iconName="chev-down" size="sm" />
      </StyledFilterSummary>
      <StyledFilterDetails isOpen={isOpen} data-automation="CountryFilterDetails">
        <CountrySelectionList
          initiallySelectedCountries={initiallySelectedCountries}
          initiallyNotSelectedCountries={initiallyNotSelectedCountries}
          selectedCountryIdsSet={selectedCountryIdsSet}
          handleClickOnCountry={handleClickOnCountry}
        />
      </StyledFilterDetails>
    </div>
  );
};
